import React, { useMemo, useRef, useState, useCallback } from 'react';

const defaultPrizes = ['৳7','৳14','৳21','৳178','৳28','৳35','৳999','৳9999'];
const defaultRestricted = new Set(['৳178','৳999','৳9999']);

export default function FreeSpin({
  apiBaseUrl,
  token = (typeof window !== 'undefined' ? localStorage.getItem('token') : null),
  logoUrl,
  prizes = defaultPrizes,
  restricted = defaultRestricted,
  onResult,
}) {
  const wheelRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(null);
  const [wonAmount, setWonAmount] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [checking, setChecking] = useState(false);

  const sliceCount = prizes.length;
  const sliceAngle = 360 / sliceCount;

  const allowedIndexes = useMemo(() => (
    prizes
      .map((p, i) => (restricted.has(p) ? -1 : i))
      .filter((i) => i !== -1)
  ), [prizes, restricted]);

  const normalize = (angle) => {
    let a = angle % 360;
    if (a < 0) a += 360;
    return a;
  };

  const centerAngleOf = (index) => index * sliceAngle + sliceAngle / 2;

  const safeParseDate = (value) => {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const hasRecentSuccessfulDeposit = (txsOrBool) => {
    if (typeof txsOrBool === 'boolean') return txsOrBool;
    if (!Array.isArray(txsOrBool)) return false;
    const now = new Date();
    const threeDaysAgo = new Date(now);
    threeDaysAgo.setDate(now.getDate() - 3);
    return txsOrBool.some((t) => {
      const action = String(t?.action || '').toLowerCase();
      const status = String(t?.status || '').toLowerCase();
      if (action !== 'deposit' || status !== 'success') return false;
      const createdAt = safeParseDate(t?.created_at);
      return createdAt && createdAt >= threeDaysAgo;
    });
  };

  const hasFreeSpinToday = (txs) => {
    if (!Array.isArray(txs)) return false;
    const today = new Date();
    return txs.some((t) => {
      const normalizedAction = String(t?.action || '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
      if (normalizedAction !== 'freespin') return false;
      const createdAt = safeParseDate(t?.created_at);
      return createdAt && createdAt.toDateString() === today.toDateString();
    });
  };

  const fetchEligibilityData = useCallback(async (authToken, remoteId) => {
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const [userRes, txRes] = await Promise.all([
        fetch(`${apiBaseUrl}/api/user/show?remote_id=${encodeURIComponent(remoteId)}`, { headers }),
        fetch(`${apiBaseUrl}/api/transaction/show?remote_id=${encodeURIComponent(remoteId)}`, { headers }),
      ]);

      if (!userRes.ok || !txRes.ok) return null;

      const userData = await userRes.json();
      const txJson = await txRes.json();
      const txs = Array.isArray(txJson?.transactions) ? txJson.transactions : (txJson?.data || txJson || []);

      const now = new Date();
      const threeDaysAgo = new Date(now);
      threeDaysAgo.setDate(now.getDate() - 3);
      threeDaysAgo.setHours(0, 0, 0, 0);
      const todayStr = now.toDateString();

      const isSuccessStatus = (t) => {
        const keyVal = t?.key;
        if (keyVal === 1 || keyVal === '1') return true;
        const st = String(t?.status ?? '').toLowerCase();
        return ['1', 'success', 'completed', 'approved', 'ok', 'done'].includes(st);
      };

      const parseDate = (t) => {
        const raw = t?.created_at || t?.createdAt || t?.date || t?.time;
        if (!raw) return null;
        if (/^\d+$/.test(String(raw))) {
          const num = Number(raw);
          const ms = num > 1e12 ? num : (num < 1e12 ? num * 1000 : num);
          const dNum = new Date(ms);
          return Number.isNaN(dNum.getTime()) ? null : dNum;
        }
        const dotFmt = /^\s*(\d{1,2})[\.\/-](\d{1,2})[\.\/-](\d{2,4})\s*$/;
        const mDot = String(raw).match(dotFmt);
        if (mDot) {
          let [_, dd, mm, yy] = mDot;
          const year = yy.length === 2 ? (Number(yy) + 2000) : Number(yy);
          const month = Number(mm) - 1;
          const day = Number(dd);
          const d = new Date(year, month, day);
          return Number.isNaN(d.getTime()) ? null : d;
        }
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? null : d;
      };

      let hasRecentDeposit = false;
      let hasFreeSpinToday = false;
      for (const t of txs) {
        const actionRaw = String(t?.action || t?.type || '');
        const normalized = actionRaw.toLowerCase().replace(/[^a-z]/g, '');
        const createdAt = parseDate(t);
        if (!createdAt) continue;
        if (!hasRecentDeposit && normalized.includes('deposit') && isSuccessStatus(t) && createdAt >= threeDaysAgo) {
          hasRecentDeposit = true;
        }
        if (!hasFreeSpinToday && normalized === 'freespin' && createdAt.toDateString() === todayStr) {
          hasFreeSpinToday = true;
        }
        if (hasRecentDeposit && hasFreeSpinToday) break;
      }

      return { userData, hasRecentDeposit, hasFreeSpinToday };
    } catch (err) {
      console.error('Eligibility fetch failed:', err);
      return null;
    }
  }, [apiBaseUrl]);

  const validateBeforeSpin = useCallback(async () => {
    const authToken = token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
    const remoteId = typeof window !== 'undefined' ? localStorage.getItem('remote_id') : null;

    if (!authToken || !remoteId) {
      setModalMessage('You must be logged in to spin.');
      return false;
    }

    const data = await fetchEligibilityData(authToken, remoteId);
    if (!data) {
      setModalMessage('Unable to verify eligibility right now. Please try again.');
      return false;
    }

    const sessionVal = String(data.userData?.session_id ?? data.userData?.data?.session_id ?? '').trim();
    if (sessionVal !== '1') {
      setModalMessage('Please verify your phone number first.');
      return false;
    }

    if (data.hasFreeSpinToday) {
      setModalMessage('You have already claimed your free spin for today!');
      return false;
    }

    if (!hasRecentSuccessfulDeposit(data.hasRecentDeposit)) {
      setModalMessage('You must deposit successfully in the last 3 days to spin.');
      return false;
    }

    return true;
  }, [token, fetchEligibilityData]);

  const spinToIndex = useCallback((targetIndex) => {
    const current = normalize(rotation);
    const center = centerAngleOf(targetIndex);
    const margin = Math.max(2, Math.min(8, sliceAngle / 6));
    const epsRange = (sliceAngle / 2) - margin;
    const epsilon = (Math.random() * 2 * epsRange) - epsRange;
    const desired = normalize(360 - (center + epsilon));
    let baseDelta = desired - current;
    if (baseDelta < 0) baseDelta += 360;
    const extraTurns = 5 + Math.floor(Math.random() * 3);
    const totalDelta = extraTurns * 360 + baseDelta;

    setSpinning(true);
    setPendingIndex(targetIndex);
    setShowResult(false);
    setRotation((prev) => prev + totalDelta);
  }, [rotation, sliceAngle]);

  const spin = useCallback(() => {
    const run = async () => {
      if (spinning || checking || allowedIndexes.length === 0) return;
      setModalMessage('');

      setChecking(true);
      const ok = await validateBeforeSpin();
      setChecking(false);
      if (!ok) return;

      const targetIndex = allowedIndexes[Math.floor(Math.random() * allowedIndexes.length)];
      spinToIndex(targetIndex);
    };
    run();
  }, [spinning, checking, allowedIndexes, spinToIndex, validateBeforeSpin]);

  const onTransitionEnd = async (ev) => {
    if (ev.propertyName !== 'transform' || !spinning) return;
    setSpinning(false);

    let idx = pendingIndex;
    let finalAmount = 0;

    if (idx == null) {
      const finalAngle = normalize(rotation);
      const pointerAngle = normalize(360 - finalAngle);
      idx = Math.floor(pointerAngle / sliceAngle);
      const over = pointerAngle - (idx * sliceAngle);
      if (over > sliceAngle / 2 && idx < sliceCount - 1) idx++;
    }

    const amtLabel = prizes[idx];
    setWonAmount(amtLabel);
    setShowResult(true);
    finalAmount = parseInt(String(amtLabel).replace(/[^\d]/g, ''), 10);

    try {
      const authToken = token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
      const res = await fetch(`${apiBaseUrl}/api/free/spin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ amount: finalAmount }),
      });

      if (res.status === 400) {
        setShowResult(false);
        setModalMessage('You have already claimed your free spin for today!');
        return;
      }
      if (!res.ok) {
        console.error('Free spin failed:', res.status);
        return;
      }
      if (onResult) onResult(finalAmount);
    } catch (err) {
      console.error('Free spin network error:', err);
    }
  };

  return (
    <div className="fs-overlay">
      <div className="fs-modal">
        <div className="fs-wheel-container">
          <div className="fs-pointer" />
          <div
            ref={wheelRef}
            className="fs-wheel"
            style={{ transform: `rotate(${rotation}deg)` }}
            onTransitionEnd={onTransitionEnd}
          >
            {prizes.map((p, i) => (
              <div
                className="fs-slice-number"
                key={i}
                style={{ '--angle': `${i * sliceAngle + sliceAngle / 2}deg` }}
              >
                {p}
              </div>
            ))}
            <div className="fs-center-circle">
              {logoUrl ? (
                <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button
            disabled={spinning || checking}
            onClick={spin}
            className={`fs-spin-btn ${!spinning && !checking ? 'fs-shake' : ''}`}
          >
            {checking ? 'Checking…' : (spinning ? 'Spinning…' : 'SPIN')}
          </button>
        </div>

        {showResult && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              🎉 Congratulations!<br />
              You won <span className="fs-amount">{wonAmount}</span><br />
              <button className="fs-close-btn" onClick={() => setShowResult(false)}>OK</button>
            </div>
          </div>
        )}

        {modalMessage && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              ⛔<br />
              {modalMessage}<br />
              <button className="fs-close-btn" onClick={() => setModalMessage('')}>OK</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
